// Receiving Hub — the warehouse user's daily workflow page.
//
// Sections (top to bottom):
//   1. Header with the packing-list, shipments and manual-receive actions
//   2. Tab bar: Inbox / Received boxes — ONE table on screen at a time
//   3. Filter bar: the shared controls (search, box scan) plus the Inbox-only
//      ones (status toggles, overdue chip, the counts line)
//   4. The active tab's table, full height in its own vessel:
//      - Inbox (purchase_order_lines, default pending+partial)
//      - Received boxes (one row per receipt_items row, barcode from its box,
//        grouped under one band per delivery; window is 7d/30d/90d/1y, the choice
//        remembered in localStorage — the warehouse asked to see past a week,
//        and the API clamps at 365)
//
// TWO TABS, NOT TWO STACKED TABLES (Mannie). Both piles are long enough to want
// the whole viewport, and stacking them meant the receipts feed lived below a
// 200-row queue nobody scrolled past. The tab is NAVIGATION, so it seeds from
// `?tab=received` and is not remembered; the filters underneath it are working
// state, so they are.
//
// An Inbox line is received on its order's page (/receiving/order/:poId, or
// /receiving/line/:lineId for a line with no order header). The one drawer left
// here is manual receive, for a box with no purchase order behind it.
//
// Placing a new purchase order is NOT a receiving flow — that form lives at
// /procurement/new (Procurement/PurchaseOrderForm.tsx), reached from the PO
// list, not from here. Design decision (Mannie, final): "POs on the PO page,
// Receiving on the Receiving page."
//
// Both tables are the shared DataTable (components/table): it owns the vessel,
// the group bands and the capability-driven column visibility. The controls stay
// on the PAGE, above the vessel: the search box and the box-scan field because
// they outlive the tab switch, the status toggles, the overdue chip and the
// counts line because they belong to a pile the vessel does not own. The one
// control that DOES ride a strip is the receipts window, which changes what that
// single table asked for.
//
// All data fetches follow the codebase pattern: useApi + useState/useEffect.
// No TanStack Query (per Phase 1b execution plan).

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { buttonClass } from '../components/ui/Button';
import { SegTrack } from '../components/ui/SegTrack';
import { Tag, type TagTone } from '../components/ui/Tag';
import { Tooltip } from '../components/ui/Tooltip';
import { Link, Redirect, useHistory, useLocation } from 'react-router-dom';
import dayjs from 'dayjs';
import type { ColumnDef } from '@tanstack/react-table';
import { useApi } from '../apiHelpers';
import { useToast } from '../components/Toast/ToastContext';
import useUserStore from '../stores/useUserStore';
import PageHeader, { PrimaryAction, SecondaryAction } from '../components/PageHeader';
import {
  FilterBar,
  FilterEnd,
  FilterSearch,
} from '../components/FilterBar';
import Chip from '../components/ui/Chip';
import { formatEventDate, formatTimestamp, parseDate } from '../utils/date-utils';
import { ScanInput } from '../components/BarcodeScanner';
import { DataTable, type GroupByOpts } from '../components/table/DataTable';
import ItemName, { itemLabel } from '../components/ItemName';
import DrawerShell from './DrawerShell';
import ReceiveDrawer from './ReceiveDrawer';
import RequestsTab, { type RequestScan } from './RequestsTab';
import { routeScan } from './scanRouting';
import { openAuthedPdf } from './openAuthedPdf';
import { usePackingListApi, type PendingSlipLine } from './packingListApi';
import PageLayout from '../components/ui/PageLayout';
import { EmptyState } from '../components/ui/EmptyState';
import FadeIn from '../components/ui/FadeIn';
import Appear from '../components/ui/Appear';
import { Skeleton, SkeletonGroup } from '../components/ui/Skeleton';
import type {
  ExpectedReceiptRow,
  ExpectedReceiptStatus,
  ReceiptItemRow,
} from './types';

type DrawerMode = null | 'manual-receive';
type HubTab = 'inbox' | 'received' | 'requests';

const HUB_TABS: { key: HubTab; label: string }[] = [
  { key: 'inbox', label: 'Inbox' },
  { key: 'received', label: 'Received boxes' },
  { key: 'requests', label: 'Requests' },
];

// INDIVIDUALLY TOGGLEABLE, not one-of-five. The old bar had an 'Inbox' pill that
// was really a pending+partial preset, so "pending plus what got cancelled" was
// unaskable and the preset's meaning was invisible. Now the preset is just the
// default selection, and this list is also the CANONICAL ORDER of the status
// param: the URL a given selection produces cannot depend on which pill was
// clicked first.
const STATUS_PILLS: { key: ExpectedReceiptStatus; label: string }[] = [
  { key: 'pending', label: 'Pending' },
  { key: 'partial', label: 'Partial' },
  { key: 'received', label: 'Received' },
  { key: 'cancelled', label: 'Cancelled' },
];

const KNOWN_STATUSES: ExpectedReceiptStatus[] = STATUS_PILLS.map((p) => p.key);
const DEFAULT_ACTIVE = new Set<ExpectedReceiptStatus>(['pending', 'partial']);

// The filters somebody works in every day, remembered per browser like the
// calendar's status chips. The receipts window keeps its own key (RECENT_DAYS_KEY
// below): it belongs to the other tab and predates this.
const HUB_PREFS_KEY = 'receivingHubPrefs';

interface HubPrefs {
  activeStatuses?: unknown;
  overdueOnly?: unknown;
}

// Guarded on both sides: a private window throws on read, and a key written by
// an older build (or by hand) is somebody else's shape.
const loadHubPrefs = (): HubPrefs | null => {
  try {
    const raw = window.localStorage.getItem(HUB_PREFS_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? (parsed as HubPrefs) : null;
  } catch {
    return null;
  }
};

/**
 * The persisted status selection, or null to mean "use the default".
 *
 * Filtered to the four the server knows, so a stale key can never resurrect a
 * status that no longer exists and ask the API for it. An array that survives
 * that filter EMPTY-handed was garbage, and falls back; an array that was
 * genuinely empty stays empty, because turning every pill off is a real
 * selection with a visible exit (the filter empty state's Clear filters).
 */
const readActiveStatuses = (prefs: HubPrefs | null): Set<ExpectedReceiptStatus> | null => {
  const raw = prefs?.activeStatuses;
  if (!Array.isArray(raw)) return null;
  const known = KNOWN_STATUSES.filter((s) => raw.includes(s));
  if (raw.length > 0 && known.length === 0) return null;
  return new Set(known);
};

const STATUS_TONES: Record<string, TagTone> = {
  pending: 'info',
  partial: 'warn',
  received: 'good',
  cancelled: 'neutral',
};

const formatCurrency = (cents: number | null) =>
  cents === null || cents === undefined ? '—' : `$${(cents / 100).toFixed(2)}`;

// Windows for the receipts feed. 365 is the API's ceiling
// (receipt-boxes-router clamps there); anything longer belongs in a report, not
// a daily working page.
const RECENT_WINDOWS = [
  { days: 7, label: '7d' },
  { days: 30, label: '30d' },
  { days: 90, label: '90d' },
  { days: 365, label: '1y' },
] as const;
const RECENT_DAYS_KEY = 'receiving.recentDays';

// Guarded on both sides, exactly like the hub prefs above. A Safari private
// window throws on every localStorage call, and a bare read in a state
// initializer throws during render, which blanks the whole page over a
// remembered window size.
const loadRecentDays = (): number | null => {
  try {
    const saved = parseInt(window.localStorage.getItem(RECENT_DAYS_KEY) || '', 10);
    return RECENT_WINDOWS.some((w) => w.days === saved) ? saved : null;
  } catch {
    return null;
  }
};

const saveRecentDays = (days: number) => {
  try {
    window.localStorage.setItem(RECENT_DAYS_KEY, String(days));
  } catch {
    /* storage disabled or full — the window just will not survive a reload */
  }
};

// A date somebody TYPED, not one the system watched happen (migration 031).
// filed_at is when the row was keyed; received_at is when the goods landed. They
// differ only on an admin backdate, and the difference should never be invisible
// — a receiving discrepancy traced back to a date nobody could tell was manual
// is a bad afternoon.
const isBackdated = (row: ReceiptItemRow): boolean => {
  if (!row.filed_at || !row.received_at) return false;
  return String(row.filed_at).slice(0, 10) !== String(row.received_at).slice(0, 10);
};

// The loading state. The shared table's loading slot is ONE full-width cell, so bars sized to
// columns that are not in that row line up with nothing. Varying widths are what make it read as
// content rather than as a loading bar.
const PULSE_WIDTHS = ['w-[70%]', 'w-[55%]', 'w-[85%]', 'w-[40%]', 'w-[60%]'];

const PulseBlock: React.FC<{ rows?: number }> = ({ rows = 4 }) => (
  <SkeletonGroup label="Loading" className="px-4 py-3 space-y-3">
    {Array.from({ length: rows }, (_, i) => (
      <Skeleton key={i} className={`h-3 ${PULSE_WIDTHS[i % PULSE_WIDTHS.length]}`} />
    ))}
  </SkeletonGroup>
);

const isOverdue = (row: ExpectedReceiptRow): boolean => {
  if (row.status !== 'pending' && row.status !== 'partial') return false;
  if (!row.expected_arrival_date) return false;
  const d = parseDate(row.expected_arrival_date);
  if (!d) return false;
  return d.isBefore(dayjs.utc().startOf('day'), 'day');
};

// ---------------------------------------------------------------------------
// The inbox's columns.
//
// Module scope, per the DataTable contract: a fresh columns array hands
// flexRender a new component TYPE and remounts every cell. Nothing here closes
// over page state. The one thing a cell needs that its own row cannot answer,
// the packing-slip claims, arrives on the table's `meta` and is read at render
// time.
//
// No sorting anywhere (no accessorFn, and the page passes no `sortable`): the
// pile is read grouped by purchase order, soonest ETA first, and a column sort
// would cut across the grouping it exists to make legible.
// ---------------------------------------------------------------------------

interface InboxMeta {
  /** Confirmed slip lines that still owe boxes, keyed by order line (038). */
  slipByLine: Map<string, PendingSlipLine>;
}

/** Where an Inbox line is received: its order's page, focused on the line. */
const receiveLineHref = (row: ExpectedReceiptRow): string =>
  row.purchase_order_id
    ? `/receiving/order/${row.purchase_order_id}?line=${encodeURIComponent(row.id)}`
    : `/receiving/line/${row.id}`;

// The house filled action (PageHeader's PRIMARY) at band scale: one per order
// band, so the full header size would stack a column of pills down the queue.
const BAND_PRIMARY = buttonClass('filled', 'sm');

const INBOX_COLUMNS: ColumnDef<ExpectedReceiptRow, any>[] = [
  {
    id: 'eta',
    header: 'ETA',
    // The overdue treatment is PER ROW, so it cannot be a static column class:
    // cellAttrs is the per-row hook, and the component folds its className into
    // the td's list.
    meta: {
      numeric: true,
      cellClass: 'whitespace-nowrap',
      cellAttrs: (r: ExpectedReceiptRow) => ({
        className: isOverdue(r) ? 'text-amber-400 font-semibold' : 'text-on-surface',
      }),
    },
    cell: ({ row }) => {
      const line = row.original;
      return (
        <>
          {line.expected_arrival_date
            ? formatEventDate(line.expected_arrival_date, 'MMM D')
            : '—'}
          {isOverdue(line) && (
            <>
              {/* A drawn dot, not an emoji: emojis are out of the UI, and a
                  glyph rendered by the OS font is nobody's brand color. */}
              <span
                aria-hidden="true"
                className="inline-block w-2 h-2 rounded-full bg-red-400 ml-1"
              />
              <span className="sr-only">overdue</span>
            </>
          )}
        </>
      );
    },
  },
  {
    // NAME, NOT SKU. The code is the database's handle on the item; the words are
    // what the person at the boxes reads off the carton and off the vendor's
    // paperwork. The SKU still identifies the item on the box detail page and on
    // the printed label, which is where somebody goes when they need it.
    id: 'item',
    header: 'Item',
    // No cellAttrs title here: ItemName puts the full label on its own wrapper,
    // which is the element that truncates. Two nested titles is one tooltip too
    // many and two places for the same string to drift.
    meta: { cellClass: 'max-w-xs' },
    cell: (info) => {
      const { slipByLine } = info.table.options.meta as InboxMeta;
      const line = info.row.original;
      const slip = slipByLine.get(line.id);
      return (
        // Flex rather than inline flow: ItemName stacks its variant detail under
        // the base name, and the chip has to stay beside the pair rather than
        // wrap under whichever line happened to be last.
        <div className="flex items-baseline gap-2">
          <ItemName size="sm" item={line} />
          {/* 038. A slip said what is coming; the order's receive page offers it
              as a one-press claim. Said on the item cell rather than in the Qty
              column because the ordered quantity there is a different fact and
              the two side by side read as a contradiction. */}
          {slip && (
            <Tooltip label={`Packing slip ${slip.slip_number || '(unnumbered)'} says ${slip.quantity_shipped} shipped`}>
              <Tag size="sm" tone="accent" className="shrink-0 tabular-nums">
                slip says {slip.quantity_outstanding}
              </Tag>
            </Tooltip>
          )}
        </div>
      );
    },
  },
  {
    id: 'revision',
    header: 'Rev',
    meta: { cellClass: 'text-on-surface-variant' },
    cell: ({ row }) => row.original.revision || '—',
  },
  {
    id: 'quantity',
    header: 'Qty',
    meta: { align: 'right', numeric: true, cellClass: 'text-on-surface' },
    // Gross over expected, with the returned count beside it when there is
    // one: a line reading 10/10 next to a partial chip is a return, not a late
    // delivery, and the dock should not have to guess which.
    cell: ({ row }) => {
      const r = row.original;
      const returned = Number(r.quantity_returned) || 0;
      return (
        <>
          {`${r.quantity_received}/${r.quantity_expected}`}
          {returned > 0 && (
            <span className="block text-[11px] text-on-surface-variant">{`${returned} returned`}</span>
          )}
        </>
      );
    },
  },
  {
    id: 'status',
    header: 'Status',
    meta: { align: 'center' },
    cell: ({ row }) => (
      <Tag tone={STATUS_TONES[row.original.status] || 'neutral'} className="capitalize">
        {row.original.status}
      </Tag>
    ),
  },
  {
    id: 'action',
    header: 'Action',
    meta: { align: 'right' },
    cell: ({ row: { original: row } }) => {
      // Only a CANCELLED line refuses a box, matching the server. A SERVICE or
      // CHARGE line refuses one too: nothing will ever arrive in a box for an NRE
      // stencil, and the server answers `sku_required_at_receive` if you try.
      //
      // These rows only reach here through the Received and Cancelled history
      // tabs; the open queue filters them out server-side, so the link has to be
      // gated here too.
      const rowReceivable =
        row.status !== 'cancelled' && (row.line_type ?? 'stock') === 'stock';
      // A closed line still takes a box, but it is not the ordinary path and
      // should not read like one: quieter styling, and a label that says what is
      // actually about to happen.
      const rowComplete = row.status === 'received';
      if (!rowReceivable) return <span className="text-xs text-on-surface-disabled">—</span>;
      return (
        <Link
          to={receiveLineHref(row)}
          // The NAME, matching the cell this link sits beside. itemLabel answers
          // with the em-dash placeholder on a row with nothing to be called, so
          // the word fallbacks stay.
          aria-label={
            rowComplete
              ? `Receive another box of ${nameOrFallback(row, 'this order')}, which is already complete`
              : `Receive ${nameOrFallback(row, 'order')}${row.vendor ? ` from ${row.vendor}` : ''}`
          }
          className={buttonClass('quiet', 'sm')}
        >
          {rowComplete ? 'Receive more' : '▸ Receive'}
        </Link>
      );
    },
  },
];

/**
 * Which order a line belongs to, for the group bands.
 *
 * Pre-009 lines have no header but MAY carry a free-text ref, and those refs
 * differ per line — lumping every null together would have hidden that, and is
 * why the per-line PO column could not simply be dropped. Key on the ref so each
 * distinct one gets its own heading; only truly ref-less lines share the last
 * bucket, which the group order forces to the end.
 */
/** What to call a row out loud, with a word to fall back on when it has no
 *  name and no code. Only the aria-labels need this: the CELLS may show the
 *  placeholder, because a blank there would be a hole in a column. */
const nameOrFallback = (row: ExpectedReceiptRow, fallback: string): string => {
  const label = itemLabel(row);
  return label === '—' ? fallback : label;
};

const inboxGroupKey = (row: ExpectedReceiptRow): string =>
  row.purchase_order_id ||
  (row.purchase_order_ref ? `ref:${row.purchase_order_ref}` : '__none__');

// ---------------------------------------------------------------------------
// One band per delivery on the Received tab: every item of one press of Receive
// shares a delivery_id, across however many boxes it was packed into.
// ---------------------------------------------------------------------------

const deliveryGroupKey = (row: ReceiptItemRow): string =>
  row.delivery_id || `box:${row.receipt_box_id}`;

const BAND_QUIET = buttonClass('quiet', 'sm');

const DeliveryBand: React.FC<{
  rows: ReceiptItemRow[];
  canViewPO: boolean;
  onReprint: (rows: ReceiptItemRow[]) => Promise<void>;
}> = ({ rows, canViewPO, onReprint }) => {
  const [reprinting, setReprinting] = useState(false);
  const first = rows[0];
  // One entry per box. The QC-hold box (every item in it rejected) numbers
  // itself 1 of 1, so it is tagged by what it is and listed last.
  const boxes = useMemo(() => {
    const byId = new Map<string, { row: ReceiptItemRow; qcHold: boolean }>();
    for (const r of rows) {
      const entry = byId.get(r.receipt_box_id);
      if (entry) entry.qcHold = entry.qcHold && r.disposition === 'rejected';
      else byId.set(r.receipt_box_id, { row: r, qcHold: r.disposition === 'rejected' });
    }
    return Array.from(byId.values()).sort(
      (a, b) => Number(a.qcHold) - Number(b.qcHold) || (a.row.box_no ?? 0) - (b.row.box_no ?? 0)
    );
  }, [rows]);
  if (!first) return null;
  const poId = rows.find((r) => r.purchase_order_id)?.purchase_order_id ?? null;
  const orderLabel = first.po_number || first.purchase_order_ref;
  const itemCount = rows.length;
  return (
    <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
      <span className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        {boxes.map(({ row: b, qcHold }) => (
          <span key={b.receipt_box_id} className="inline-flex items-baseline gap-1.5">
            <Link
              to={`/receiving/box/${b.receipt_box_id}`}
              className="font-mono text-sm text-on-surface hover:text-primary hover:underline"
            >
              {b.barcode}
            </Link>
            {qcHold ? (
              <span className="text-xs text-amber-300">QC Hold</span>
            ) : (
              (b.box_count ?? 1) > 1 && (
                <span className="text-xs text-on-surface-variant tabular-nums">
                  box {b.box_no} of {b.box_count}
                </span>
              )
            )}
          </span>
        ))}
      </span>
      {orderLabel &&
        (poId && canViewPO ? (
          <Link
            to={`/procurement/po/${poId}`}
            className="font-mono text-xs text-primary hover:underline"
          >
            {orderLabel}
          </Link>
        ) : (
          <span className="font-mono text-xs text-on-surface-variant">{orderLabel}</span>
        ))}
      {first.vendor && <span className="text-xs text-on-surface-variant">{first.vendor}</span>}
      <span className="ml-auto text-xs text-on-surface-variant tabular-nums">
        Received {formatTimestamp(first.received_at, 'MMM D')}
        {first.received_by_name ? ` by ${first.received_by_name}` : ''}. {itemCount} item
        {itemCount === 1 ? '' : 's'}.
      </span>
      <button
        type="button"
        disabled={reprinting}
        aria-label={`Reprint the box sticker${boxes.length === 1 ? '' : 's'} for ${boxes
          .map((b) => b.row.barcode)
          .join(', ')}`}
        onClick={async () => {
          setReprinting(true);
          try {
            await onReprint(rows);
          } finally {
            setReprinting(false);
          }
        }}
        className={BAND_QUIET}
      >
        {reprinting ? 'Reprinting…' : 'Reprint'}
      </button>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Recent receipts. Module scope for the same reason, and no meta at all: every
// cell here is pure over its own row. Cost is a column def like any other —
// `columnVisibility` is what gates it, so the capability check lives in one
// place instead of in the markup twice.
// ---------------------------------------------------------------------------

const RECENT_COLUMNS: ColumnDef<ReceiptItemRow, any>[] = [
  {
    id: 'received',
    header: 'Received',
    meta: { numeric: true, cellClass: 'text-on-surface whitespace-nowrap' },
    cell: ({ row }) => (
      <>
        {formatTimestamp(row.original.received_at, 'MMM D')}
        {isBackdated(row.original) && (
          <Tooltip label={`Arrival date entered by hand. Filed ${formatEventDate(row.original.filed_at, 'MMM D')}.`}>
            <Tag size="sm" tone="warn" className="ml-1.5 align-middle">backdated</Tag>
          </Tooltip>
        )}
      </>
    ),
  },
  {
    id: 'barcode',
    header: 'Barcode',
    meta: { cellClass: 'font-mono text-xs text-on-surface-variant' },
    cell: ({ row }) => row.original.barcode,
  },
  {
    // Same swap as the Inbox: the name is the fact, the code is the handle. The
    // barcode column beside it is already the mono thing the eye lands on.
    id: 'item',
    header: 'Item',
    meta: { cellClass: 'max-w-xs' },
    cell: ({ row }) => <ItemName size="sm" item={row.original} />,
  },
  {
    id: 'quantity',
    header: 'Qty',
    meta: { align: 'right', numeric: true, cellClass: 'text-on-surface' },
    cell: ({ row }) => row.original.quantity_received,
  },
  {
    id: 'remaining',
    header: 'Remaining',
    meta: { align: 'right', numeric: true, cellClass: 'text-on-surface-variant' },
    cell: ({ row }) => row.original.quantity_remaining,
  },
  {
    // The response has always carried the uuid and no screen showed a name, so
    // "who received this" needed a database query.
    id: 'received_by',
    header: 'Received by',
    meta: { cellClass: 'text-on-surface whitespace-nowrap' },
    cell: ({ row }) =>
      row.original.received_by_name || <span className="text-on-surface-disabled">&mdash;</span>,
  },
  {
    id: 'cost',
    header: 'Cost',
    meta: { numeric: true, cellClass: 'text-on-surface-variant' },
    cell: ({ row }) => formatCurrency(row.original.unit_cost_cents),
  },
  {
    id: 'detail',
    header: 'Detail',
    meta: { align: 'right' },
    cell: ({ row }) => (
      <Link
        to={`/receiving/item/${row.original.id}`}
        aria-label={`View detail for ${itemLabel(row.original)} in box ${row.original.barcode}`}
        className="text-xs text-primary hover:underline"
      >
        View →
      </Link>
    ),
  },
];

const ReceivingHubPage: React.FC = () => {
  const api = useApi();
  const toast = useToast();
  const history = useHistory();
  const user = useUserStore((s) => s.user);
  // Strict: cost visibility is opt-in even for admins (see require-financial).
  const canSeeFinancials = !!user.can_financial;

  const canAccess =
    user.isadmin || user.can_admin || user.can_operations || user.can_manufacturing || user.can_procurement;

  // Same check the /procurement/po/:id route itself uses (App.js) — the hub
  // renders for operations/manufacturing too, and those users would otherwise
  // see a "View PO" link that immediately redirects them back to '/'.
  const canViewPO = user.isadmin || user.can_admin || user.can_procurement;

  // --- Which pile is on screen ---
  //
  // NAVIGATION, so deliberately not persisted: somebody who looked up a box
  // yesterday should still land on the queue this morning.
  const [activeTab, setActiveTab] = useState<HubTab>('inbox');

  // --- Filter state ---
  //
  // Initialized FROM storage rather than defaulted and then corrected by an
  // effect: the first fetch has to carry the selection somebody left here, and a
  // load-after-mount would fire a fetch for the defaults first and overwrite the
  // saved key on the way past.
  const [activeStatuses, setActiveStatuses] = useState<Set<ExpectedReceiptStatus>>(
    () => readActiveStatuses(loadHubPrefs()) ?? new Set(DEFAULT_ACTIVE),
  );
  const [overdueOnly, setOverdueOnly] = useState<boolean>(() => {
    const prefs = loadHubPrefs();
    return typeof prefs?.overdueOnly === 'boolean' ? prefs.overdueOnly : false;
  });
  const [search, setSearch] = useState('');

  // Persist the working filters. The first run writes back what the
  // initializers just read, so it is a no-op in content; the only thing it
  // changes is normalizing a key that held an unknown status.
  //
  // Written in pill order, not in click order: one selection has one
  // serialization, so the key reads the way the bar looks.
  useEffect(() => {
    try {
      window.localStorage.setItem(
        HUB_PREFS_KEY,
        JSON.stringify({
          activeStatuses: KNOWN_STATUSES.filter((s) => activeStatuses.has(s)),
          overdueOnly,
        }),
      );
    } catch {
      /* storage disabled or full — the filters just will not survive a reload */
    }
  }, [activeStatuses, overdueOnly]);

  const toggleStatus = (s: ExpectedReceiptStatus) => {
    setActiveStatuses((prev) => {
      const next = new Set(prev);
      if (next.has(s)) next.delete(s);
      else next.add(s);
      return next;
    });
  };

  // A link may arrive already asking for something, as
  // /receiving?q=PO-FF-TEC26205. Seed the search from the URL
  // and keep following it if a new link lands while the page is mounted.
  // Typing in the box deliberately does NOT write back to the URL — the param
  // is an entrance, not state.
  const location = useLocation();
  useEffect(() => {
    const q = new URLSearchParams(location.search).get('q');
    if (q) setSearch(q);
  }, [location.search]);

  // Same entrance, for the tab: /receiving?tab=received. Matches the PO list's
  // ?tab= seeding, membership check included, so an unknown value leaves the
  // default alone instead of landing on a tab that does not exist.
  useEffect(() => {
    const tab = new URLSearchParams(location.search).get('tab');
    if (tab && HUB_TABS.some((t) => t.key === tab)) setActiveTab(tab as HubTab);
  }, [location.search]);

  // --- Data state ---
  const [inbox, setInbox] = useState<ExpectedReceiptRow[]>([]);
  const [recent, setRecent] = useState<ReceiptItemRow[]>([]);
  const [loadingInbox, setLoadingInbox] = useState(true);
  const [loadingRecent, setLoadingRecent] = useState(true);

  // How far back the receipts feed looks. Was hardcoded to 7 days, which the
  // warehouse asked to change: a week cannot settle "did that March delivery
  // ever land". Remembered per browser, because somebody who works in the
  // 90-day view works in it every day and should not re-pick it each morning.
  const [recentDays, setRecentDays] = useState<number>(() => loadRecentDays() ?? 7);

  // --- Drawer state ---
  const [drawer, setDrawer] = useState<DrawerMode>(null);
  const [manualReceiveDirty, setManualReceiveDirty] = useState(false);

  // ── WHAT A PACKING LIST ALREADY TOLD US (038) ──────────────────────────────
  //
  // Confirmed slip lines that still have boxes owing, keyed by the order line they
  // point at. A row with one of these wears a "slip says N" chip.
  //
  // A SEPARATE FETCH rather than a join onto the inbox, deliberately. The inbox
  // read is the hot path on this page and runs on every keystroke of the search
  // box; this one is small, changes only when somebody records a shipment, and is
  // additive — if it fails, the hub works exactly as it did before this existed.
  const packingListApi = usePackingListApi();
  const [slipLines, setSlipLines] = useState<PendingSlipLine[]>([]);

  const fetchSlipLines = useCallback(async () => {
    try {
      const data = await packingListApi.pendingLines();
      setSlipLines(data.rows || []);
    } catch {
      // Silent on purpose. The chip is a convenience on top of a flow that
      // works without it, and a toast here would blame the person for a failure
      // that changes nothing they were about to do.
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * The slip line for one inbox row, or null.
   *
   * FIRST match wins when a line somehow has two outstanding slip lines (two
   * partial shipments against one order line, which is legitimate). The feed is
   * ordered newest ship date first, so the first is the most recent delivery,
   * which is the one somebody is standing in front of.
   */
  const slipByLine = useMemo(() => {
    const map = new Map<string, PendingSlipLine>();
    for (const row of slipLines) {
      if (!map.has(row.purchase_order_line_id)) map.set(row.purchase_order_line_id, row);
    }
    return map;
  }, [slipLines]);

  // ── SCAN A BOX, GET THE BOX ────────────────────────────────────────────────
  //
  // Every box that goes through this page leaves with an RB- sticker on it, and
  // until now the only way back from the sticker to the record was to remember
  // the barcode and search the recent-receipts list — which only reaches back as
  // far as the window you happen to have selected.
  //
  // ScanInput handles both ways a barcode arrives: a keyboard-wedge gun typing
  // into the focused field and terminating with Enter, and a phone camera through
  // the lazy-loaded ZXing modal. Reused rather than rebuilt — it is the same
  // component the asset and drone lookups use, so a scanner that works on one
  // works here.
  const [boxQuery, setBoxQuery] = useState('');
  const [boxLooking, setBoxLooking] = useState(false);

  const lookUpBox = useCallback(async (raw: string) => {
    const code = raw.trim();
    if (!code) return;
    setBoxLooking(true);
    try {
      // The endpoint tolerates a bare short id and adds the RB- itself, so a
      // half-read sticker or somebody typing the eight characters both work.
      const box = (await api.get(`/receipt-boxes/by-barcode/${encodeURIComponent(code)}`)) as {
        id: string;
        items?: { id: string }[];
        scanned_item_id?: string | null;
      };
      setBoxQuery('');
      // An item sticker names its item. A box sticker on a box holding one item
      // lands on that item, where every old sticker always landed. A box holding
      // several lands on the box.
      const items = box.items || [];
      const itemId = box.scanned_item_id || (items.length === 1 ? items[0].id : null);
      history.push(itemId ? `/receiving/item/${itemId}` : `/receiving/box/${box.id}`);
    } catch (err: any) {
      // NOT a toast for the 404. A sticker that resolves to nothing is the
      // ordinary outcome of a misread, and the person is standing there holding
      // the box — they need the code they scanned repeated back so they can see
      // it is wrong, not a banner that slides away.
      if (err?.status === 404) {
        toast.warning(`No box with barcode ${code.toUpperCase().startsWith('RB-') ? code.toUpperCase() : `RB-${code.toUpperCase()}`}. Check the sticker.`);
      } else {
        toast.error(err?.data?.error?.message || err?.message || 'Could not look that box up');
      }
    } finally {
      setBoxLooking(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // A shipping label goes to the Requests tab, which does its own lookup.
  const [requestScan, setRequestScan] = useState<RequestScan | null>(null);
  const handleScan = (raw: string) => {
    const route = routeScan(raw);
    if (route === 'box') lookUpBox(raw);
    else if (route === 'request') {
      setRequestScan((prev) => ({ raw: raw.trim(), nonce: (prev?.nonce || 0) + 1 }));
      setActiveTab('requests');
      setBoxQuery('');
    }
  };

  // The inbox needs the same guard as the feed below it, and for the same reason:
  // both are driven by the search box, so typing produces overlapping requests and a
  // slow answer for "SD" can land after a fast one for "SD-ESC". Pre-existing, but
  // leaving one of the pair unguarded once the other has it is worse than either.
  // This is the table people click Receive from.
  const inboxRequestRef = useRef(0);

  const fetchInbox = useCallback(async () => {
    const seq = (inboxRequestRef.current += 1);
    // Server-side filter for status. Built off STATUS_PILLS, so the param is the
    // same string for the same selection however the pills were clicked.
    const statusParam = STATUS_PILLS.filter((p) => activeStatuses.has(p.key))
      .map((p) => p.key)
      .join(',');
    // NOTHING SELECTED IS AN ANSWER, and it is "no rows", not "every row". Sending
    // `status=` would let the server decide what an empty filter means, which for
    // a queue that gates a Receive button is the wrong place to find out. Claiming
    // the sequence first invalidates whatever was in flight.
    if (!statusParam) {
      setInbox([]);
      setLoadingInbox(false);
      return;
    }
    setLoadingInbox(true);
    try {
      const params = new URLSearchParams({
        status: statusParam,
        page_size: '200',
      });
      if (overdueOnly) params.set('overdue', 'true');
      if (search.trim()) params.set('q', search.trim());
      const data = (await api.get(`/expected-receipts?${params.toString()}`)) as {
        rows: ExpectedReceiptRow[];
      };
      if (seq !== inboxRequestRef.current) return;
      setInbox(data.rows || []);
    } catch (err: any) {
      if (seq !== inboxRequestRef.current) return;
      toast.error(err?.message || 'Failed to load inbox');
    } finally {
      if (seq === inboxRequestRef.current) setLoadingInbox(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeStatuses, overdueOnly, search]);

  // WHICH REQUEST IS ALLOWED TO WIN. The feed is now keyed on the search box, so
  // typing produces overlapping requests and a slow answer for "FF-E" can land
  // after a fast one for "FF-ESC" and overwrite it: the table then shows results
  // for a query that is no longer in the box. The debounce narrows that window
  // and does not close it. A monotonic counter does: only the newest request may
  // write state.
  const recentRequestRef = useRef(0);

  const fetchRecent = useCallback(async () => {
    const seq = (recentRequestRef.current += 1);
    setLoadingRecent(true);
    try {
      // limit scales with the window, or a year's view would silently stop at
      // the default 100 rows and read as "that is all there was".
      const limit = Math.min(500, Math.max(100, recentDays * 3));
      // ONE SEARCH BOX, BOTH TABLES. It used to narrow the inbox above and leave
      // this feed showing everything, which reads as the search being broken.
      //
      // Sent to the server rather than filtered here, because `limit` bounds the
      // response: a client-side filter would search only the rows that fit under
      // it and quietly miss the rest, worst on the 1-year window where somebody
      // is searching precisely because scrolling will not do.
      const params = new URLSearchParams({ days: String(recentDays), limit: String(limit) });
      if (search.trim()) params.set('q', search.trim());
      const data = (await api.get(`/receipt-boxes?${params.toString()}`)) as {
        rows: ReceiptItemRow[];
      };
      if (seq !== recentRequestRef.current) return;
      setRecent(data.rows || []);
    } catch (err: any) {
      if (seq !== recentRequestRef.current) return;
      toast.error(err?.message || 'Failed to load recent receipts');
    } finally {
      // Only the newest request clears the skeleton, or a superseded response
      // would stop the spinner while its replacement is still in flight.
      if (seq === recentRequestRef.current) setLoadingRecent(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recentDays, search]);

  // Debounce search 250ms; status, overdue and the window trigger immediately.
  //
  // ONE TIMER PER FETCH, NOT ONE SHARED TIMER. Both callbacks now depend on
  // `search`, so each needs the debounce, but they must not share an effect:
  // `fetchInbox` also depends on the status pills and the overdue checkbox, so a
  // shared effect refetched the whole receipts feed every time somebody clicked
  // a status pill, and a shared one keyed only on `search` would have gone stale
  // for whichever fetch had other inputs. Separate effects give each one exactly
  // its own triggers.
  useEffect(() => {
    const t = setTimeout(() => fetchInbox(), search ? 250 : 0);
    return () => clearTimeout(t);
  }, [fetchInbox, search]);

  useEffect(() => {
    const t = setTimeout(() => fetchRecent(), search ? 250 : 0);
    return () => clearTimeout(t);
  }, [fetchRecent, search]);

  useEffect(() => {
    fetchSlipLines();
  }, [fetchSlipLines]);

  // GROUPED BY PURCHASE ORDER.
  //
  // A delivery arrives as an order, not as a scattering of unrelated lines, and
  // the flat list made you scan the PO column to work out which rows belonged
  // to the box in front of you. Lines stay INDIVIDUALLY receivable — a vendor
  // shipping three of five lines this week is the normal case, not an edge one,
  // so the group is a heading, never a batch action.
  //
  // Headerless lines (NULL purchase_order_id: pre-009 rows, wrapper rows whose
  // vendor never resolved) collect under their own heading at the END rather
  // than one group each, which is what keying on a null would have produced.
  //
  // Built in a memo because the band needs page data the row does not carry:
  // canViewPO decides whether the PO number is a link, and the group ORDER needs
  // an ETA per key that `order` — which sees only the keys — cannot derive. Both
  // come off one derivation here, so the order and the bands cannot disagree.
  const inboxGroupBy = useMemo<GroupByOpts<ExpectedReceiptRow>>(() => {
    // Soonest ETA first, so the most urgent order is at the top; a group with no
    // dated line sorts last rather than to the front on an empty string.
    const earliestEta = new Map<string, string>();
    for (const row of inbox) {
      const key = inboxGroupKey(row);
      const min = earliestEta.get(key) ?? '';
      const eta = row.expected_arrival_date;
      earliestEta.set(key, eta && (!min || eta < min) ? eta : min);
    }
    const etaOf = (key: string) => earliestEta.get(key) || '9999-12-31';
    return {
      key: inboxGroupKey,
      // The band's CONTENT is rich (a link, chips, a summary), so the vessel's
      // microlabel default (11px uppercase tracked) is wrong here: it would
      // case-transform vendor NAMES. Padding only; the tone and the primary
      // hairline still come from the component's row default.
      cellClass: 'px-4 py-2',
      // The component's default is alphabetical by key, which here would be
      // alphabetical by UUID.
      order: (keys) =>
        [...keys].sort((a, b) => {
          if (a === '__none__') return 1;
          if (b === '__none__') return -1;
          return etaOf(a).localeCompare(etaOf(b));
        }),
      // One heading per order, carrying the way in: the order's receive page,
      // where every line that turned up goes in with one press. A headerless
      // group has no order page, so its lines link one at a time.
      renderHeader: (key, rows) => {
        const first = rows[0];
        const poId = first?.purchase_order_id ?? null;
        const label = first?.po_number || first?.purchase_order_ref || 'No purchase order';
        return (
          <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
            {poId && canViewPO ? (
              <Link
                to={`/procurement/po/${poId}`}
                className="font-mono text-sm text-primary hover:underline"
              >
                {label}
              </Link>
            ) : (
              <span className={`font-mono text-sm ${poId ? 'text-on-surface' : 'text-on-surface-variant italic'}`}>
                {label}
              </span>
            )}
            {first?.vendor && <span className="text-xs text-on-surface-variant">{first.vendor}</span>}
            {/* The ruling is "approval gates the document, not the goods": these
                lines are receivable. The chip exists so the person at the dock
                KNOWS the order is unreleased instead of the rule being invisible.
                Same words as the PO chip — one state, one name. */}
            {first?.po_status === 'pending_approval' && (
              <Tooltip label="This order has not been approved yet. Receiving is still allowed; approval gates the document, not the goods.">
                <Tag tone="warn">Pending approval</Tag>
              </Tooltip>
            )}
            <span className="text-xs text-on-surface-variant tabular-nums ml-auto">
              {rows.length} line{rows.length === 1 ? '' : 's'}
              {/* Same word as the line pill below — one state, one name, not a
                  fifth rendering of 'partial'. */}
              {rows.some((r) => r.status === 'partial') && (
                <span className="text-amber-400/90"> · partial</span>
              )}
              {rows.some(isOverdue) && <span className="text-amber-400"> · overdue</span>}
            </span>
            {poId &&
              rows.some(
                (r) => r.status !== 'cancelled' && (r.line_type ?? 'stock') === 'stock',
              ) && (
                <Link
                  to={`/receiving/order/${poId}`}
                  aria-label={`Receive this order, ${label}`}
                  className={BAND_PRIMARY}
                >
                  Receive this order
                </Link>
              )}
          </div>
        );
      },
    };
  }, [inbox, canViewPO]);

  // Every box of the delivery is re-rendered from the live rows first, so the
  // one PDF that opens carries today's contents rather than the first print's.
  const reprintDelivery = useCallback(async (rows: ReceiptItemRow[]) => {
    const boxIds = Array.from(new Set(rows.map((r) => r.receipt_box_id)));
    const deliveryId = rows[0]?.delivery_id;
    try {
      await Promise.all(boxIds.map((id) => api.post(`/receipt-boxes/${id}/reprint-label`)));
      await openAuthedPdf(
        deliveryId
          ? `/receipt-boxes/delivery/${deliveryId}/labels.pdf`
          : `/receipt-boxes/${boxIds[0]}/label.pdf`,
      );
    } catch (err: any) {
      toast.error(
        err?.data?.error?.message ||
          'The stickers did not reprint. Try again, or reprint from the box page.',
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ONE BAND PER DELIVERY, newest first. The feed is ordered by filed_at, so the
  // group order is derived the same way: a delivery sits where its latest item
  // was keyed.
  const receivedGroupBy = useMemo<GroupByOpts<ReceiptItemRow>>(() => {
    const newest = new Map<string, string>();
    for (const row of recent) {
      const key = deliveryGroupKey(row);
      const t = String(row.filed_at || row.received_at || '');
      if (t > (newest.get(key) ?? '')) newest.set(key, t);
    }
    return {
      key: deliveryGroupKey,
      cellClass: 'px-4 py-2',
      order: (keys) =>
        [...keys].sort((a, b) => (newest.get(b) ?? '').localeCompare(newest.get(a) ?? '')),
      renderHeader: (_key, rows) => (
        <DeliveryBand rows={rows} canViewPO={canViewPO} onReprint={reprintDelivery} />
      ),
    };
  }, [recent, canViewPO, reprintDelivery]);

  // The capability gate, in one place: the Cost column def always exists and
  // visibility decides whether it renders a th and a td at all.
  const recentVisibility = useMemo(() => ({ cost: canSeeFinancials }), [canSeeFinancials]);

  const counts = useMemo(() => {
    const overdue = inbox.filter(isOverdue).length;
    return { total: inbox.length, overdue };
  }, [inbox]);

  // WHICH EMPTY STATE IS HONEST. The teaching one may only appear when nothing
  // has been narrowed, and a selection restored from localStorage counts as
  // narrowing even though this session did not do it: somebody who left the
  // pills on Cancelled last week must not be told "nothing is expected right
  // now" when the queue is full of pending lines.
  const isDefaultSelection = useMemo(
    () =>
      activeStatuses.size === DEFAULT_ACTIVE.size &&
      Array.from(DEFAULT_ACTIVE).every((s) => activeStatuses.has(s)),
    [activeStatuses],
  );

  const clearInboxFilters = () => {
    setActiveStatuses(new Set(DEFAULT_ACTIVE));
    setOverdueOnly(false);
    setSearch('');
  };

  if (!canAccess) return <Redirect to="/" />;

  const handleCloseDrawer = () => setDrawer(null);

  const handleReceived = () => {
    handleCloseDrawer();
    fetchInbox();
    fetchRecent();
  };

  return (
    <PageLayout width="wide" kind="workspace" className="pt-4" header={(
      <PageHeader
        className="mb-5"
        title="Receiving Hub"
        helpTitle="About receiving"
        lede="Where stock enters the system: every box that arrives is received here."
        primary={
          /* 038. THE PAGE'S ONE FILLED ACTION, unlike manual receive beside it,
             and the difference is what each one does to the record. This one only
             ADDS what a vendor said, changes nothing about the order, and is the
             fast path for a delivery that has paperwork. Making it hard to find
             would leave somebody typing eleven quantities beside a button that
             would have done it. */
          <PrimaryAction to="/receiving/packing-list">Read a packing list</PrimaryAction>
        }
        actions={
          <>
            {/* The record of the slips already read. A place to go and look:
                reading a new one is the daily act, looking an old one up is the
                occasional one. */}
            <SecondaryAction variant="text" to="/receiving/shipments">
              Shipments
            </SecondaryAction>
            {/* DEMOTED, deliberately, and quiet on purpose. Every use bypasses the
                approval threshold, the vendor-hold check and the spend record.
                Still here, because both reference ERPs keep it: ERPNext gates it
                behind Buying Settings.po_required rather than removing it, and the
                real cases (warranty, sample, card purchase) are rare rather than
                gone. Removing it would mean somebody either invents a purchase
                order or does not record the stock, and unrecorded stock is worse. */}
            <SecondaryAction variant="text" onClick={() => setDrawer('manual-receive')}>
              Receive without a purchase order
            </SecondaryAction>
          </>
        }
        help={
          <>
            <p>
              This is where stock enters the system. Every box that arrives gets received
              here, which puts it on the shelf, gives it a barcode label, and starts its
              history. Nothing counts as inventory until it has been through this page.
            </p>
            <p className="text-on-surface font-medium">The two ways in</p>
            <p>
              <span className="text-on-surface">Expected:</span> the Inbox tab. These are
              lines from purchase orders somebody already placed, so the item, vendor and
              price are known. Receive opens the order&apos;s own page, where every line
              that turned up goes in at once: count each one, leave the rest blank, and
              press Receive once. One PIN, and one tab of stickers to print. This is the
              normal path.
            </p>
            <p>
              <span className="text-on-surface">Receive without a purchase order:</span> for
              a box with no order behind it, like a sample, a warranty replacement, or
              something bought on a card. You pick the item yourself, and if it is not in
              the catalog yet you can add it right there without leaving the form.
            </p>
            {/* THE SENTENCE THAT CAUSED A REAL INCIDENT. This used to end "If the item
                is not in the catalog yet, it has to be created first", which stopped
                being true when the drawer got inline minting in ERP chunk 1. Somebody
                read it, concluded Manual Receive was not open to them, and raised two
                purchase orders to receive goods that had been bought with no purchase
                order at all. Both had to be removed by hand.

                So the fix is not only deleting the false clause: the case it covers is
                now stated in the words somebody standing at a box would use. */}
            <p className="text-on-surface-variant">
              <span className="text-on-surface">
                You never need a purchase order to receive something.
              </span>{' '}
              If a box turned up and there is no order behind it, Receive without a
              purchase order is the whole answer. Do not raise a purchase order for goods that are already
              here: it records a commitment that was never made, and nothing in this app
              can delete it afterwards.
            </p>
            <p className="text-on-surface font-medium">Receiving more or less than expected</p>
            <p>
              Type what actually arrived, always. Short shipments leave the line open for
              the rest. An over-shipment inside the order's allowance is recorded and
              flagged; past it the receive is refused until an admin approves it, with the
              numbers in front of them. The system would rather know the truth than have a
              tidy number.
            </p>
            <p className="text-on-surface font-medium">Where it goes</p>
            <p>
              Every box is filed to a location. Usually that is your warehouse. It can
              also be a vendor who holds your material, like a contract assembler you
              drop-shipped parts to. Stock at a vendor stays on the books but does{' '}
              <span className="text-on-surface">not</span> count toward what you can build,
              because it is not somewhere anyone can pick it up. There is no way to record
              it being used up yet, so treat filing something there as recording a fact,
              not as a step in a workflow.
            </p>
            <p className="text-on-surface font-medium">Backdating</p>
            <p>
              Admins can set the date a box actually arrived, for when paperwork catches
              up days later. The stock movement is dated to match, so it lands in the
              right month for the books. Backdated rows are marked on the Received boxes
              tab, so a date somebody typed is never mistaken for one the system watched
              happen.
            </p>
            <p className="text-on-surface font-medium">Packing lists</p>
            <p>
              If a box came with a slip taped to it, read it first. Photograph it and it works
              out which purchase order it belongs to and what the shipper says is in the box,
              then takes you to that order&apos;s receive page with the slip&apos;s numbers
              offered beside each line. You still count: what you count wins over what the
              paper claims.
            </p>
            <p className="text-on-surface font-medium">Old labels still work</p>
            <p>
              If a SKU has been renamed, scanning or typing the old code still finds the
              item. The app will tell you what it is called now. Nothing on a shelf needs
              relabeling.
            </p>
          </>
        }
      />
    )}>
    <div className="space-y-5">
      {/* Which pile. NAVIGATION, not a filter: it switches which list is on
          screen rather than narrowing one, so it sits above the bar rather than
          inside it. */}
      <SegTrack
        label="Which receiving list to show"
        options={HUB_TABS.map((t) => ({ key: t.key, label: t.label }))}
        value={activeTab}
        onChange={(k) => setActiveTab(k as HubTab)}
      />

      {/* Filter bar. The search and the box scan drive both tabs and stay put;
          everything else here is about the queue and travels with it. */}
      <FilterBar>
        {activeTab === 'inbox' && (
          <>
            {/* INDIVIDUALLY TOGGLEABLE, so multi: a thumb cannot be in three
                places, and "pending plus what got cancelled" is a real question. */}
            <SegTrack
              multi
              label="Which line statuses to show"
              options={STATUS_PILLS.map((p) => ({ key: p.key, label: p.label }))}
              value={activeStatuses}
              onChange={(k) => toggleStatus(k as ExpectedReceiptStatus)}
            />
            <Chip pressed={overdueOnly} onClick={() => setOverdueOnly(!overdueOnly)}>
              Overdue only
            </Chip>
          </>
        )}
        {activeTab !== 'requests' && (
          <FilterSearch
            value={search}
            onChange={setSearch}
            label="Search the Inbox and received boxes by SKU, vendor, purchase order, or box barcode"
            placeholder="Search SKU, vendor, PO, barcode…"
          />
        )}
        {/* A SEPARATE control from the search beside it, deliberately. They look
            similar and answer opposite questions: the search filters both of this
            page's lists, this LEAVES the page for one box. The search does match
            barcodes now, which makes the distinction matter more rather than
            less: scanning a sticker into the wrong field would filter a table
            down to one row instead of opening the box you are holding. */}
        <div className="w-[190px]">
          {/* ScanInput takes `id` and no aria-label, so the label is a real one,
              visually hidden. A placeholder is not a label: it disappears the
              moment somebody types, which is exactly when a screen reader user
              needs it. */}
          <label htmlFor="box-barcode" className="sr-only">
            Scan or type a box barcode to open it
          </label>
          <ScanInput
            id="box-barcode"
            value={boxQuery}
            onChange={setBoxQuery}
            onSubmit={handleScan}
            autoSubmitOnScan
            disabled={boxLooking}
            placeholder={boxLooking ? 'Looking…' : 'Box or label'}
            scanTitle="Scan a box sticker or shipping label"
            scanHint="Point at the RB- barcode on a box, or the tracking barcode on a shipping label."
          />
        </div>
        {/* Counts the queue, so it goes where the queue is. The receipts tab
            carries its own box count in its strip. */}
        {activeTab === 'inbox' && (
          <FilterEnd>
            <span role="status" aria-live="polite">
              {counts.total} row{counts.total === 1 ? '' : 's'}
              <Appear show={counts.overdue > 0} className="text-amber-400 ml-2">· {counts.overdue} overdue</Appear>
            </span>
          </FilterEnd>
        )}
      </FilterBar>

      {/* Inbox table. The vessel is the DataTable's — the page's own box, header
          band and row borders are gone, and the h2 rides the working strip where
          it names the pile it labels. The <section> stays only to carry the
          landmark its aria-labelledby names.

          UNMOUNTED, not hidden, when the other tab is up: stickyHeader measures
          the vessel's top edge off the DOM, and a display:none element measures
          zero, so a CSS-hidden table would come back with a wrong cap. Neither
          vessel holds state worth preserving across a switch. */}
      {activeTab === 'inbox' && (
        <FadeIn>
        <section aria-labelledby="inbox-heading">
          <DataTable<ExpectedReceiptRow>
            data={inbox}
            columns={INBOX_COLUMNS}
            getRowId={(r) => r.id}
            meta={{ slipByLine }}
            groupBy={inboxGroupBy}
            // The queue scrolls inside its own vessel instead of growing the page
            // by 200 rows (Mannie): header holds, the field carries the movement,
            // and the page's own chrome stays in view above it.
            stickyHeader
            strip={
              <h2 id="inbox-heading" className="text-sm font-semibold text-on-surface">
                Inbox
              </h2>
            }
            loading={loadingInbox}
            loadingContent={<PulseBlock rows={5} />}
            empty={
              /* Two different empty states wearing one row. "Nothing is expected"
                 and "your filter matched nothing" are opposite problems, and the
                 second one has an obvious exit. */
              isDefaultSelection && !overdueOnly && !search.trim() ? (
                <EmptyState variant="area" title="Nothing is expected right now.">
                  Lines land here when a purchase order is submitted. If a box
                  turned up without one, use &quot;Receive without a purchase
                  order&quot; above.
                </EmptyState>
              ) : (
                <EmptyState
                  variant="area"
                  action={(
                    <button
                      type="button"
                      onClick={clearInboxFilters}
                      className={buttonClass('tonal', 'md')}
                    >
                      Clear filters
                    </button>
                  )}
                >
                  {activeStatuses.size === 0
                    ? 'No statuses selected, so there is nothing to show.'
                    : 'No rows match that filter.'}
                </EmptyState>
              )
            }
          />
        </section>
        </FadeIn>
      )}

      {/* Received boxes. The heading and the window control both ride the strip:
          the component's band is a flex row with min-w-0, so `ml-auto` parks the
          control on the right exactly where justify-between had it. */}
      {activeTab === 'received' && (
        <FadeIn>
        <section aria-labelledby="recent-heading">
          <DataTable<ReceiptItemRow>
            data={recent}
            columns={RECENT_COLUMNS}
            getRowId={(r) => r.id}
            groupBy={receivedGroupBy}
            columnVisibility={recentVisibility}
            // Its own tab now, so it gets the same treatment the queue does: the
            // feed scrolls inside its vessel with the window control held above it.
            stickyHeader
            strip={
              <>
                {/* SAME WORDS AS THE TAB, like the Inbox heading above. One pile,
                    one name; the window control beside it is what says how far
                    back this view reaches. */}
                <h2 id="recent-heading" className="text-sm font-semibold text-on-surface">
                  Received boxes
                  <span className="text-on-surface-variant font-normal ml-1.5 tabular-nums">
                    ({recent.length} box{recent.length === 1 ? '' : 'es'})
                  </span>
                </h2>
                {/* The house segmented control, same as the tab strip above, so
                    every control on this page that changes what the field shows
                    looks like the same kind of control. */}
                <SegTrack
                  className="ml-auto"
                  label="How far back to show receipts"
                  options={RECENT_WINDOWS.map((w) => ({ key: String(w.days), label: w.label }))}
                  value={String(recentDays)}
                  onChange={(k) => {
                    const days = Number(k);
                    setRecentDays(days);
                    saveRecentDays(days);
                  }}
                />
              </>
            }
            loading={loadingRecent}
            loadingContent={<PulseBlock />}
            empty={
              /* "Nothing arrived" and "your search matched nothing" are opposite
                 facts and the second one must not read as the first: a receiver who
                 searched a SKU and saw "nothing received in the last 30 days" would
                 conclude the box was never filed and file it again. */
              search.trim() ? (
                /* THE WINDOW IS THE USUAL REASON, NOT THE SEARCH. A box
                   received 26 days ago is invisible in the 7-day default, and
                   "no boxes match" then reads as "that box does not exist".
                   One button beats a sentence telling somebody to go and find
                   the right control themselves. */
                <EmptyState
                  variant="area"
                  title={(
                    <>
                      No boxes match{' '}
                      <span className="font-mono">{search.trim()}</span> in{' '}
                      {recentDays === 365 ? 'the last year' : `the last ${recentDays} days`}.
                    </>
                  )}
                  action={recentDays !== 365 ? (
                    <button
                      type="button"
                      onClick={() => {
                        setRecentDays(365);
                        saveRecentDays(365);
                      }}
                      className={buttonClass('tonal', 'sm')}
                    >
                      Search the last year instead
                    </button>
                  ) : undefined}
                >
                  It may be further back than{' '}
                  {recentDays === 365 ? 'a year' : `${recentDays} days`}.
                </EmptyState>
              ) : (
                <EmptyState
                  variant="area"
                  title={`Nothing received in ${recentDays === 365 ? 'the last year' : `the last ${recentDays} days`}.`}
                >
                  Boxes show up here the moment they are received, whether from the
                  Inbox tab or a manual receive.
                </EmptyState>
              )
            }
          />
        </section>
        </FadeIn>
      )}

      {activeTab === 'requests' && (
        <FadeIn>
          <RequestsTab scan={requestScan} onScanTaken={() => setRequestScan(null)} />
        </FadeIn>
      )}

      {/* Drawers */}
      {drawer === 'manual-receive' && (
        <DrawerShell title="Manual Receive" onClose={handleCloseDrawer} dirty={manualReceiveDirty}>
          <ReceiveDrawer
            onDirtyChange={setManualReceiveDirty}
            onClose={handleCloseDrawer}
            onReceived={handleReceived}
          />
        </DrawerShell>
      )}
    </div>
    </PageLayout>
  );
};

export default ReceivingHubPage;
